import React, { useState, useEffect, useCallback } from 'react';
import stockReorderApi, { errorMessage } from '../../../api/stockReorderApi';

const PAGE_SIZE = 100;

/**
 * The part number → supplier map for one branch.
 *
 * The ERP workbook is an importer, not the live source: it lands here once and
 * every reorder run afterwards reads these rows instead of re-parsing a 1.3 MB
 * spreadsheet. That is also what makes single-row editing possible.
 *
 * Import is additive by default. A manual correction has to survive the next
 * ERP export, so "add new only" never rewrites a row that already exists —
 * replacing the branch wholesale is a separate, confirmed action.
 */
const SupplierMapCard = ({ businessUnitId, branchName, onToast }) => {
    const [stats, setStats] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [search, setSearch] = useState('');
    const [onlyConflicts, setOnlyConflicts] = useState(false);

    const [file, setFile] = useState(null);
    const [mode, setMode] = useState('sync_from_erp');
    const [result, setResult] = useState(null);

    const [newPart, setNewPart] = useState('');
    const [newSupplier, setNewSupplier] = useState('');

    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const loadStats = useCallback(async () => {
        if (!businessUnitId) return setStats(null);
        try {
            setStats(await stockReorderApi.supplierMapStats(businessUnitId));
        } catch (err) {
            setError(errorMessage(err, 'Could not read the supplier map'));
        }
    }, [businessUnitId]);

    const loadRows = useCallback(
        async (nextOffset = 0) => {
            if (!businessUnitId) {
                setRows([]);
                setTotal(0);
                return;
            }
            setBusy(true);
            setError('');
            try {
                const data = await stockReorderApi.listSupplierMap(businessUnitId, {
                    search,
                    onlyConflicts,
                    limit: PAGE_SIZE,
                    offset: nextOffset
                });
                setRows(data.rows || []);
                setTotal(data.total || 0);
                setOffset(nextOffset);
            } catch (err) {
                setError(errorMessage(err, 'Could not load the supplier map'));
            } finally {
                setBusy(false);
            }
        },
        [businessUnitId, search, onlyConflicts]
    );

    useEffect(() => {
        loadStats();
        loadRows(0);
        setResult(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessUnitId]);

    const runImport = async () => {
        if (
            mode === 'replace_all' &&
            !window.confirm(
                `Replace the entire map for ${branchName || 'this branch'}?\n\nEvery existing row is deleted first, including any single-part corrections made by hand. This cannot be undone.`
            )
        )
            return;

        setBusy(true);
        setError('');
        setResult(null);
        try {
            const res = await stockReorderApi.importSupplierMap(businessUnitId, { mode, file });
            setResult(res);
            onToast?.(
                res.mode === 'replace_all'
                    ? `Replaced the map — ${res.added} mapping(s) imported.`
                    : res.mode === 'sync_from_erp'
                    ? `Synced — ${res.added} added, ${res.updated} updated. Stock and Reorder now use it.`
                    : `Imported ${res.added} new mapping(s); ${res.skipped_existing} already existed.`
            );
            setFile(null);
            await loadStats();
            await loadRows(0);
        } catch (err) {
            setError(errorMessage(err, 'Import failed'));
        } finally {
            setBusy(false);
        }
    };

    const addRow = async () => {
        setBusy(true);
        setError('');
        try {
            await stockReorderApi.addSupplierMapRow({
                businessUnitId,
                partNumber: newPart,
                supplierName: newSupplier
            });
            onToast?.(`Mapped ${newPart.trim()} → ${newSupplier.trim()}.`);
            setNewPart('');
            setNewSupplier('');
            await loadStats();
            await loadRows(offset);
        } catch (err) {
            setError(errorMessage(err, 'Could not add that mapping'));
        } finally {
            setBusy(false);
        }
    };

    const saveEdit = async (row) => {
        if (!editValue.trim() || editValue.trim() === row.supplier_name) {
            setEditingId(null);
            return;
        }
        setBusy(true);
        setError('');
        try {
            const updated = await stockReorderApi.updateSupplierMapRow(row.id, editValue.trim());
            setRows((list) => list.map((r) => (r.id === row.id ? updated : r)));
            setEditingId(null);
            onToast?.(`${row.part_key} → ${updated.supplier_name}.`);
            await loadStats();
        } catch (err) {
            setError(errorMessage(err, 'Could not save that mapping'));
        } finally {
            setBusy(false);
        }
    };

    const removeRow = async (row) => {
        if (!window.confirm(`Remove the mapping for ${row.part_key}? It will show as Unmapped.`))
            return;
        setBusy(true);
        setError('');
        try {
            await stockReorderApi.deleteSupplierMapRow(row.id);
            onToast?.(`Mapping for ${row.part_key} removed.`);
            await loadStats();
            await loadRows(offset);
        } catch (err) {
            setError(errorMessage(err, 'Could not remove that mapping'));
        } finally {
            setBusy(false);
        }
    };

    if (!businessUnitId) {
        return (
            <section className="sr-card">
                <h2 className="sr-card-title">Supplier map</h2>
                <div className="sr-empty">Pick a branch above to see its supplier map.</div>
            </section>
        );
    }

    const page = Math.floor(offset / PAGE_SIZE) + 1;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <>
            <section className="sr-card">
                <h2 className="sr-card-title">Import from ERP</h2>

                {stats && (
                    <div className="sr-stats">
                        <div className="sr-stat">
                            <span className="sr-stat-value">{stats.total.toLocaleString()}</span>
                            <span className="sr-stat-label">Mappings</span>
                        </div>
                        <div className="sr-stat">
                            <span className="sr-stat-value">{stats.suppliers.toLocaleString()}</span>
                            <span className="sr-stat-label">Suppliers</span>
                        </div>
                        <div className="sr-stat">
                            <span className="sr-stat-value">{stats.manual.toLocaleString()}</span>
                            <span className="sr-stat-label">Edited by hand</span>
                        </div>
                        <div className={`sr-stat ${stats.conflicts ? 'sr-stat-warn' : ''}`}>
                            <span className="sr-stat-value">{stats.conflicts.toLocaleString()}</span>
                            <span className="sr-stat-label">Unresolved conflicts</span>
                        </div>
                    </div>
                )}

                {stats?.total === 0 && (
                    <div className="sr-alert sr-alert-warn">
                        <strong>Nothing imported for this branch yet</strong>
                        <p>
                            Reorder runs are still reading this branch's stored supplier workbook
                            directly. Import it once below and they switch to the database — faster,
                            and you can then fix a single part without re-uploading anything.
                        </p>
                    </div>
                )}

                <div className="sr-field">
                    <label htmlFor="sr-map-file">Supplier file</label>
                    <input
                        id="sr-map-file"
                        type="file"
                        accept=".xls,.xlsx,.xlsm,.csv,.htm,.html"
                        onChange={(e) => setFile(e.target.files[0] || null)}
                    />
                    <span className="sr-hint">
                        Needs <strong>Part Number</strong> and <strong>Supplier Name</strong>{' '}
                        columns. Leave empty to import the branch's stored supplier workbook.
                    </span>
                </div>

                <div className="sr-field">
                    <span className="sr-label">How to handle parts already in the map</span>
                    <div className="sr-checklist">
                        <label className="sr-check">
                            <input
                                type="radio"
                                name="sr-import-mode"
                                checked={mode === 'sync_from_erp'}
                                onChange={() => setMode('sync_from_erp')}
                            />
                            Sync with ERP (recommended) — add new, update changed suppliers, keep
                            manual edits
                        </label>
                        <label className="sr-check">
                            <input
                                type="radio"
                                name="sr-import-mode"
                                checked={mode === 'add_new'}
                                onChange={() => setMode('add_new')}
                            />
                            Add new only — existing mappings are left untouched
                        </label>
                        <label className="sr-check">
                            <input
                                type="radio"
                                name="sr-import-mode"
                                checked={mode === 'replace_all'}
                                onChange={() => setMode('replace_all')}
                            />
                            Replace everything — deletes this branch's map first
                        </label>
                    </div>
                </div>

                <div className="sr-actions">
                    <button className="sr-btn sr-btn-primary" onClick={runImport} disabled={busy}>
                        {busy ? 'Importing…' : 'Import'}
                    </button>
                </div>

                {result && (
                    <div className="sr-alert sr-alert-info">
                        <strong>
                            {result.rows_in_file.toLocaleString()} row(s) read from {result.file}
                        </strong>
                        <p>
                            {result.added.toLocaleString()} added ·{' '}
                            {result.updated > 0 && `${result.updated.toLocaleString()} updated · `}
                            {result.skipped_existing.toLocaleString()} already existed and were left
                            alone
                            {result.kept_manual > 0 &&
                                ` · ${result.kept_manual.toLocaleString()} manual mapping(s) kept although the ERP differs`}
                            {result.replaced > 0 &&
                                ` · ${result.replaced.toLocaleString()} removed first`}
                            {result.conflicts > 0 &&
                                ` · ${result.conflicts.toLocaleString()} part(s) had more than one supplier in the file`}
                        </p>
                    </div>
                )}

                {error && <div className="sr-alert sr-alert-error">{error}</div>}
            </section>

            <section className="sr-card">
                <h2 className="sr-card-title">
                    Supplier map <span className="sr-muted">({total.toLocaleString()})</span>
                </h2>

                <div className="sr-inline-form">
                    <input
                        type="text"
                        placeholder="Part number"
                        value={newPart}
                        onChange={(e) => setNewPart(e.target.value)}
                    />
                    <input
                        type="text"
                        placeholder="Supplier name"
                        value={newSupplier}
                        onChange={(e) => setNewSupplier(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && newPart.trim() && newSupplier.trim()) addRow();
                        }}
                    />
                    <button
                        className="sr-btn sr-btn-primary"
                        onClick={addRow}
                        disabled={busy || !newPart.trim() || !newSupplier.trim()}
                    >
                        Add mapping
                    </button>
                </div>

                <div className="sr-filters">
                    <input
                        type="search"
                        placeholder="Search part or supplier…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') loadRows(0);
                        }}
                    />
                    <label className="sr-check">
                        <input
                            type="checkbox"
                            checked={onlyConflicts}
                            onChange={(e) => setOnlyConflicts(e.target.checked)}
                        />
                        Conflicts only
                    </label>
                    <button className="sr-btn sr-btn-sm" onClick={() => loadRows(0)} disabled={busy}>
                        Search
                    </button>
                </div>

                <div className="sr-table-wrap">
                    <table className="sr-table">
                        <thead>
                            <tr>
                                <th>Part</th>
                                <th>Supplier</th>
                                <th>Source</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr
                                    key={row.id}
                                    className={row.candidates?.length > 1 ? 'sr-row-flag' : ''}
                                >
                                    <td>
                                        {row.part_key}
                                        {row.candidates?.length > 1 && (
                                            <span
                                                className="sr-tag sr-tag-warn"
                                                title={`The file had several suppliers for this part: ${row.candidates
                                                    .map((c) => `${c.name} (${c.count})`)
                                                    .join(', ')}. Editing the supplier settles it.`}
                                            >
                                                ⚠
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {editingId === row.id ? (
                                            <input
                                                className="sr-cell-input"
                                                autoFocus
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onBlur={() => saveEdit(row)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') e.target.blur();
                                                    if (e.key === 'Escape') setEditingId(null);
                                                }}
                                            />
                                        ) : (
                                            <button
                                                type="button"
                                                className="sr-link"
                                                title="Click to change the supplier"
                                                onClick={() => {
                                                    setEditingId(row.id);
                                                    setEditValue(row.supplier_name);
                                                }}
                                            >
                                                {row.supplier_name}
                                            </button>
                                        )}
                                    </td>
                                    <td className="sr-muted">
                                        {row.source === 'manual' ? 'edited' : 'imported'}
                                    </td>
                                    <td className="sr-right">
                                        <button
                                            className="sr-btn sr-btn-sm sr-btn-danger"
                                            onClick={() => removeRow(row)}
                                            disabled={busy}
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="sr-empty">
                                        {busy ? 'Loading…' : 'No mappings match.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {total > PAGE_SIZE && (
                    <div className="sr-actions">
                        <button
                            className="sr-btn sr-btn-sm"
                            onClick={() => loadRows(Math.max(0, offset - PAGE_SIZE))}
                            disabled={busy || offset === 0}
                        >
                            ← Previous
                        </button>
                        <span className="sr-muted">
                            Page {page} of {pages}
                        </span>
                        <button
                            className="sr-btn sr-btn-sm"
                            onClick={() => loadRows(offset + PAGE_SIZE)}
                            disabled={busy || offset + PAGE_SIZE >= total}
                        >
                            Next →
                        </button>
                    </div>
                )}
            </section>
        </>
    );
};

export default SupplierMapCard;
